import apiBaseUrl from '../../utils/config.js';
import { authFetch } from '../../utils/authFetch.js';

async function parseResponse(response) {
  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

function buildError(response, data, fallbackMessage) {
  const validationMessage = Array.isArray(data?.errors)
    ? data.errors
        .map((item) => item?.message || item?.defaultMessage || item)
        .filter(Boolean)
        .join(' ')
    : '';

  const error = new Error(
    validationMessage ||
      data?.message ||
      data?.error ||
      fallbackMessage ||
      `HTTP ${response.status}`
  );

  error.status = response.status;
  error.data = data;
  return error;
}

function requirePositiveId(value, label) {
  const id = Number(value);

  if (!Number.isInteger(id) || id <= 0) {
    throw new Error(`A valid ${label} is required.`);
  }

  return id;
}

class AuthorizationClinicalProfileService {
  constructor() {
    this.baseUrl = `${apiBaseUrl}/clinical-notes/authorization-profiles`;
    this.bodyPartBaseUrl = `${apiBaseUrl}/clinical-notes/templates/body-parts`;
  }

  async getByAuthorization(authId) {
    const id = requirePositiveId(authId, 'authorization ID');

    const response = await authFetch(`${this.baseUrl}/${id}`, {
      method: 'GET',
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw buildError(
        response,
        data,
        'Authorization clinical profile could not be loaded.'
      );
    }

    return data || {};
  }

  async saveForAuthorization(authId, request) {
    const id = requirePositiveId(authId, 'authorization ID');

    const response = await authFetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw buildError(
        response,
        data,
        'Authorization clinical profile could not be saved.'
      );
    }

    return data || {};
  }

  async getActiveBodyParts() {
    const response = await authFetch(`${this.bodyPartBaseUrl}/active`, {
      method: 'GET',
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      throw buildError(
        response,
        data,
        'Active body parts could not be loaded.'
      );
    }

    return Array.isArray(data) ? data : [];
  }
}

export default new AuthorizationClinicalProfileService();
